import React, { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import AutopayEnrollment from './autopay-enrollment/AutopayEnrollment';
import {
  getAutopayDetails,
  getAutopayDetailsPromptData,
  getLegalName,
} from '../../../ducks/selectors';
import {
  AutopayTermsCardWrapper,
  AutopayTermsTitle,
  Heading,
  HR,
  Paragraph,
  Section,
  SubHeading,
  Terms,
  HyperLink,
} from './AutopayTermsCard.styles';
import AutopayPaymentInformation from '../../future-packages/autopay-payment-information';
import { formatDate } from '../../../utils/dateUtils';
import { formatCurrency } from '../../../utils/CurrencyUtils';
import { useDualEventTracking } from '../../../utils/useDualEventTracking';

const AutopayTermsCard = (props: { vehicleId: any }) => {
  const autopayDetails = useSelector(getAutopayDetails);
  const { vehicleId } = props;

  const autopayDetailsPrompt = useSelector(getAutopayDetailsPromptData);
  const legalName = useSelector(getLegalName);

  const { trackEvent } = useDualEventTracking();

  const autopayTermsRef = useRef();

  const amount = formatCurrency(autopayDetails.withdrawAmount);

  useEffect(() => trackEvent('ConfirmAutoPay Screen'), []);

  return (
    <>
      <AutopayTermsTitle>
        Make a Payment to Your Bridgecrest Account
      </AutopayTermsTitle>

      <AutopayTermsCardWrapper>
        <Terms ref={autopayTermsRef as any}>
          <Paragraph>
            Name of Buyer or Co-Buyer (“you” and “your”):{' '}
            <SubHeading>{legalName}</SubHeading>
          </Paragraph>
          <Section>
            <Paragraph>
              The “Contract” is your motor vehicle retail installment contract.
              The terms “we,” “us,” and “our” means Bridgecrest Credit Company,
              LLC and its assigns, successors, and designated service providers.
              The electronic funds transfer system we use to initiate
              transactions to your deposit account is called the Automated
              Clearing House (“ACH”).
            </Paragraph>
          </Section>
          <Section>
            <Heading>YOUR AUTHORIZATION FOR ACH PAYMENTS</Heading>
            <AutopayPaymentInformation
              autopayDetails={autopayDetails}
              autopayDetailsPrompt={autopayDetailsPrompt}
            />
            <Paragraph>
              You agree to help us verify your deposit account information upon
              request, by providing a voided check, a deposit slip and other
              assistance and information.
            </Paragraph>
          </Section>
          <Heading>
            VOLUNTARY AUTHORIZATION OF ELECTRONIC (ACH) PAYMENTS
          </Heading>
          <Section>
            <SubHeading>Recurring ACH Payments</SubHeading>
            <Paragraph>
              Starting {formatDate(new Date().toString())} you authorize us to
              initiate a series of recurring ACH charges (debits) of {amount} to
              your deposit account on a monthly basis. You authorize us to apply
              the funds we receive in payment of your Contract and to continue
              making these charges to your deposit account on or near the same
              date(s) each month until the earlier of the date the Contract is
              paid in full or the charges are cancelled by you, your depository
              institution or by us.
            </Paragraph>
          </Section>
          <Section>
            <SubHeading>Cancelling Payments and Other Terms</SubHeading>
            <Paragraph>
              We may cancel these ACH charges to your deposit account at any
              time and for any reason, without giving you advance notice. Even
              if this happens, you must still make your Contract payments on or
              before the due dates scheduled in the Contract. You may cancel our
              authorization to make ACH charges to your deposit account at any
              time by notifying us or by notifying your depository institution.
              The party you notify must be allowed a reasonable period of time
              to process your cancellation request (at least three (3) business
              days for multiple non-recurring ACH payments and for recurring ACH
              payments). Contact your depository institution directly for more
              information about how to cancel ACH charges through the depository
              institution. To notify us of any ACH cancellation request or any
              ACH charge you believe was made in error, you must either write to
              us at Bridgecrest Credit Company, LLC, 7300 E. Hampton Ave., Suite
              101, Mesa, AZ 85209; call us at{' '}
              <HyperLink href="tel:8009678526" type="external">800-967-8526</HyperLink>; or
              submit the information to us electronically using the Payment
              Options page and forms for our website (Bridgecrest.com). You
              certify the information shown is correct and complete to the best
              of your knowledge and that you are individually authorized to have
              funds deducted from the designated deposit account to make
              Contract payments. You agree we did not require you to sign this
              form and that you voluntarily elected to do so for your
              convenience and to make payments required by the Contract. You
              agree to keep sufficient funds in your deposit account to pay the
              full amount of these payments on the dates scheduled. If an ACH
              charge to your deposit account is returned to us unpaid due to
              insufficient funds, you understand and agree that we may initiate
              additional ACH charges to your deposit account after that in an
              effort to collect payment to the extent allowed by the ACH rules.
              You understand that you are liable to us for any fees that may be
              due under the Contract if any payment is late or returned to us
              unpaid, as allowed by the Contract and applicable law. You agree
              that the ACH transactions authorized by this form complies with
              all applicable laws.
            </Paragraph>
          </Section>
          <HR />
        </Terms>
        <AutopayEnrollment
          vehicleId={vehicleId}
          autopayTermsRef={autopayTermsRef}
        />
      </AutopayTermsCardWrapper>
    </>
  );
};

export default AutopayTermsCard;
